import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Data, Router } from "@angular/router";
import { HttpResponse } from "@angular/common/http";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subscription, Observable } from "rxjs";
import { tap, mergeMap, switchMap, map } from "rxjs/operators";
import { ApplicationBaseComponent } from "../../application-base.component";
import { LoaderService } from "../../../../common/services/loader.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { DomainDefinitionService } from "../../../../builder/services/domain-definition.service";
import { ApplicationsService } from "../../../services/applications.service";
import { AuthService } from "../../../../services/auth.service";
import { WorkflowData, ApplicationState, ApplicationSteps, WorkflowFieldData, WorkflowFieldFile, PaymentSteps, ApplicationPaymentParameters, ApplicationType, ApplicationPermitResult, ApplicationStateChangeRequest } from "../../../models/applications.models";
import { CasingTypeModel, FormationModel, DrillingEquipmentModel } from "../../../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { ApplicationConfigService, ApplicationsConfig, ModuleConfig, ExpeditingConfig } from "../../../../services/application-config.service";
import { ApplicationPayment, Payment } from "../../../../payments/models/paymentItem.model";
import { SidePanelService } from "../../../../common/services/side-panel.service";
import { ApplicationNotesService } from "../../../services/application-notes.service";
import { DocumentManagementService } from "../../../../common/services/document-management.service";
import { parseFileNameFromHeaders } from "../../../../common/models/onBaseIntegrationAndMapping.model";
import { SystemSetting, SystemSettingSearchSpec, SettingNames } from "../../../../admin/models/system-setting.model";
import { SystemSettingsService } from "../../../../admin/services/system-settings.service";
import { OnBaseDocumentTypeProcessReference } from '../../../../common/models/onBaseIntegrationAndMapping.model';


@Component({
    selector: 'app-stratigraphic-application-review',
    templateUrl: './stratigraphic-application-review.component.html',
    styleUrls: ['./stratigraphic-application-review.component.scss'],
    standalone: false
})
export class StratigraphicApplicationReviewComponent extends ApplicationBaseComponent implements OnInit, OnDestroy {

    agencies: Array<any> = [];

    application: WorkflowData;

    private _applicationPaymentParameters: ApplicationPaymentParameters;

    get applicationPaymentParameters(): ApplicationPaymentParameters {
        return this._applicationPaymentParameters;
    }

    casingTypes: CasingTypeModel[] = [];

    private _config: ApplicationsConfig;

    get config(): ApplicationsConfig {
        return this._config;
	}

	expediteSetting: SystemSetting;

	expediteConfig: ExpeditingConfig;

    get fees(): ApplicationPayment[] {
        if (this.form == null) return [];
        let group = this.form.get('payment');
        if (group == null) return [];
        let ctrl = group.get('payments');
        if (ctrl == null) return [];
        return ctrl.value;
    }

    set fees(value: ApplicationPayment[]) {
        if (this.form == null) return;
        let group = this.form.get('payment');
        if (group == null) return;
        let ctrl = group.get('payments');
        if (ctrl == null) return;
        ctrl.setValue(value);
    }
    drillingEquipment: DrillingEquipmentModel[];

    formations: FormationModel[];

    get isDrill(): boolean {
        if (this.form == null) return null;
        let permitForm = this.form.get('permitType');
        if (permitForm == null) return null;
        let ctrl = permitForm.get('type');
        if (ctrl == null) return null;
        return ctrl.value == 'Drill';
    }

    onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];


    approvedDialog: Dialog = new Dialog("Application #[APPNUMBER] has been approved.", [new DialogButton("OK", true, () => { this.home(); })]);
    returnDialog: Dialog = new Dialog("Application #[APPNUMBER] has been [STATE]", [new DialogButton("OK", true, () => { this.home(); })]);
    submittedDialog: Dialog = new Dialog("Application #[APPNUMBER] has been submitted for next-level review.", [new DialogButton("OK", true, () => { this.home(); })]);
    invalid: Dialog = new Dialog("Your form has missing / incomplete fields and cannot be submitted.  Please review your form for accuracy and try again.", [new DialogButton("OK", false, () => { this.resetInvalid(); })]);


    @ViewChild("permitLink") public downloadLink: ElementRef;


    get showFeeSimple(): boolean {
        if (this.form == null) return false;
        const permitTypeForm = this.form.get('permitType');
        if (permitTypeForm == null) return false;
        const ctrl = permitTypeForm.get('expedite');
        if (ctrl == null) return false;
        return ctrl.value == "true" || ctrl.value == true;
    }

    subheader: SubHeader = new SubHeader({ title: "", buttons: [{ name: "Approve", method: () => this.confirmApprove() }, { name: "Return", method: () => this.returnApplication() }, { name: "Save", method: () => this.save(false) }, { name: "Previous Section", method: () => this.previous() }, { name: "Next Section", method: () => this.next() }, { name: "View Notes & Timeline", method: () => this.viewTimeline() }] });

    get subheaderText(): string {
        return "Application # [XXX] - <span class='red'>[REVIEW]</span>";
    }

    private validStringCasingTypeCodes: string[] = ['DP*', 'COND', 'MS1', 'SURF', 'I1', 'I2', 'PROD', 'PROD2', 'HOL1', 'L1']

   


    constructor(_loader: LoaderService, _dialog: DynamicDialogService, _auth: AuthService, _subheader: SubHeaderService, _service: ApplicationsService, private route: ActivatedRoute,
        private domainService: DomainDefinitionService, private configService: ApplicationConfigService, private builder: FormBuilder, _router: Router, _sidePanelService: SidePanelService,
		_notesService: ApplicationNotesService, private _documentService: DocumentManagementService, private _settings: SystemSettingsService) {
        super(_loader, _dialog, _subheader, _auth, _service, _router, _sidePanelService, _notesService);
        this.numericFields = ['applicationStep', 'wellInformation|surfaceLatitude', 'wellInformation|surfaceLongitude', 'wellInformation|targetLatitude', 'wellInformation|targetLongitude', 'wellInformation|civilTownshipNumber', 'wellInformation|surfaceElevation', 'wellInformation|proposedTotalDepth', 'waterSource|latitude', 'waterSource|longitude', 'waterSource|withdrawalRate', 'waterSource|totalVolume', 'waterSource|recycledWater', 'access|latitude', 'access|longitude'];
        this.booleanFields = ['wellInformation|surfaceRights'];
        this.fileFields = ['documentation|restorationPlan', 'documentation|plat', 'documentation|aerialPhotography', 'documentation|aerialPhotographyWithContours', 'documentation|urbanAreaSwornStatementAffidavit', 'documentation|miscellaneousDocuments', 'review|specialConditions|twentyFortyAcre', 'review|specialConditions|annualDisposal', 'review|specialConditions|annularDisposal', 'review|specialConditions|ashtabulaDevonianFluidRotary', 'review|specialConditions|ashtabulaDevonianOriskany', 'review|specialConditions|enhancedRecovery', 'review|specialConditions|conductorMineStringComboBasket', 'review|specialConditions|directionalDrilling', 'review|specialConditions|failureToMaintainInsurance', 'review|specialConditions|fluidRotaryLakePlainOriskany', 'review|specialConditions|horizontalDrilling', 'review|specialConditions|hydrogenSulfide', 'review|specialConditions|ielSuperfund', 'review|specialConditions|knoxUnconformity', 'review|specialConditions|lakePlainRegionOriskany', 'review|specialConditions|lakePlainRegionUtica', 'review|specialConditions|mineStringBasketVoid', 'review|specialConditions|mineStringBasketGreaterThreeHundred', 'review|specialConditions|mineStringBasketLessThanThreeHundred', 'review|specialConditions|municipalWellheadProtection', 'review|specialConditions|municipalWellheadProtectionQueenston', 'review|specialConditions|noPerforationZone', 'review|specialConditions|noiseMitigation', 'review|specialConditions|oldWellUnknownStatus', 'review|specialConditions|otherConditions', 'review|specialConditions|prePermittingShaleSite', 'review|specialConditions|prePermittingUrbanSite', 'review|specialConditions|removalOfFluidHorizontalShale', 'review|specialConditions|seismicActivity', 'review|specialConditions|shallowGas', 'review|specialConditions|ssm1', 'review|specialConditions|ssm4', 'review|specialConditions|swiw', 'review|specialConditions|surfaceCasingMineStringCombo', 'review|specialConditions|surfaceCasingMineStringComboBasket', 'review|specialConditions|thinGlacialTillQueenston', 'review|specialConditions|thinGlacialTillClinton', 'review|specialConditions|trempealeau', 'review|specialConditions|wellInAOR'];
        this.dateFields = ['review|completeDate'];
        this.paymentFields = ['payment|payments', 'payments|feesDue'];
        this.authorizationFields = ['payment|creditAuthorization'];
		this.trueBooleanFields = ['permitType|expedite', 'wellInformation|surfaceGISValidationRun', 'signature|affirmation', 'waterSource|watershedValidated', 'documentation|confidential'];
        this.arrayFields = ['wellInformation|proposedFormation', 'wellInformation|agency'];

    }

    ngOnInit() {
        super.ngOnInit();
        let subs$: Subscription = this.route.data.pipe(
            tap((data: Data) => {
                this.application = data.application;
                this.onBaseDocumentTypes = data.documentTypes;
                this.subheader.title = this.subheaderText.replace("[XXX]", this.application.applicationNumber).replace('[REVIEW]', this.application.applicationState);
                this.subheaderService.updateSubHeader(this.subheader);
			}),
			mergeMap(() => {
				let spec: SystemSettingSearchSpec = {
					settingName: SettingNames.AllowExpedite
				}
				return this._settings.search(spec).pipe(
					tap((settings: SystemSetting[]) => {
						if (settings != null && settings.length > 0) {
							this.expediteSetting = settings.find(s => s.settingName === SettingNames.AllowExpedite);
						}
					})
				)
			}),
            mergeMap(() => {
                return this.domainService.getCasingStrings().pipe(
                    tap((models: CasingTypeModel[]) => {
                        this.casingTypes = [];
                        this.validStringCasingTypeCodes.forEach(c => {
                            let match = models.find(m => m.code === c);
                            if (match != null) this.casingTypes.push(match);
                        });
                    })
                )
            }),
            mergeMap(() => {
                return this.domainService.getFormations().pipe(
                    tap((models: FormationModel[]) => {                       
                        this.formations = models;
                    })
                )
            }),
            mergeMap(() => {
                return this.domainService.getDrillingEquipment().pipe(
                    tap((models: DrillingEquipmentModel[]) => {
                        var select = new DrillingEquipmentModel();
                        select.code = "";
                        select.definition = "";
                        models.unshift(select);
                        this.drillingEquipment = models;
                    })
                )
            }),
            mergeMap(() => {
                return this.domainService.getValues("AGENCY_NAME").pipe(
                    tap((values: any[]) => {
                        this.agencies = values;
                    })
                )
            }),
            mergeMap(() => {
                return this.configService.getModuleConfig().pipe(
                    tap((conf: ModuleConfig) => {
						if (conf != null) {
							if (conf.applications != null) {
								this._config = conf.applications;
								if (this._config.permits != null && this._config.permits.documents != null) this.documentConfig = this._config.permits.documents;
							}
							if (conf.expediteFees != null) {
								this.expediteConfig = conf.expediteFees;
							}
						}
                    })
                )
            })
        ).subscribe(() => {
            this._loader.toggleLoader(false);
            if (this.auth.User.groups.includes(this.application.workflowMetadata.assignedGroupId) == false && this.application.workflowMetadata.assignedUserId != this.auth.User.id) this.unauthorized();            
            this.form = this.buildForm();
            this.form.get('applicationStep').setValue(ApplicationSteps.Review);
            this.generateApplicationPaymentParameters();
            this.form.markAsPristine();
            this.allowDeactivate.next(true);
            this.formReady$.next(true);
        },
            (error) => {
                console.error(error);
                this.dialogService.showError();
                this._loader.toggleLoader(false);
            });
        this.subscription.add(subs$);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    approve(): void {
        let nextState: ApplicationState =
            (this.application.applicationState == ApplicationState.FinalApproval)
                ? ApplicationState.Approved
                : (this.application.applicationState == ApplicationState.InitialApproval)
                    ? ApplicationState.FinalApproval
                        : (this.application.applicationState == ApplicationState.InitialReview)
                            ? ApplicationState.InitialApproval
                            : ApplicationState.InitialReview;

        let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {             
                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: nextState
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            }),
            tap((app: WorkflowData) => {
                let dialog: Dialog = (app.applicationState != ApplicationState.Approved)
                    ? this.submittedDialog
                    : this.approvedDialog;
                dialog.message = dialog.message.replace('[APPNUMBER]', app.applicationNumber);
                this._loader.toggleLoader(false);
                dialog.toggleDialog(true);
                this.dialogService.updateDialog(dialog);
            })
        );
        if (nextState == ApplicationState.Approved) {
            obs$ = this.saveProgress().pipe(
                switchMap((app: WorkflowData) => {
                    let request: ApplicationStateChangeRequest = {
                        applicationId: app.id,
                        currentState: app.applicationState,
                        requestedState: nextState
                    } as ApplicationStateChangeRequest;
                    return this.dataService.routeApplication(request);
                }),
                switchMap((app: WorkflowData) => {
                    this.application = app;
                    return this.dataService.generatePermit(app, true);
                }),
                switchMap((resp: ApplicationPermitResult) => {
                    this._loader.toggleLoader(true);
                    return this._documentService.downloadFromQueue(resp.queuedDocumentId);
                }),
                map((resp: HttpResponse<Blob>) => {
                    this._loader.toggleLoader(false);
                    if (resp != null) {
                        const fileName = parseFileNameFromHeaders(resp.headers.get("content-disposition"));
                        const blob = new Blob([resp.body], { type: resp.body.type });
						const blobUri = window.URL.createObjectURL(blob);
						const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
						anchor.href = blobUri;
						anchor.download = fileName;
						anchor.click();
                    }
                    return this.application;
                }),
                tap((app: WorkflowData) => {
                    let dialog: Dialog = (app.applicationState != ApplicationState.Approved)
                        ? this.submittedDialog
                        : this.approvedDialog;
                    dialog.message = dialog.message.replace('[APPNUMBER]', app.applicationNumber);
                    this._loader.toggleLoader(false);
                    dialog.toggleDialog(true);
                    this.dialogService.updateDialog(dialog);
                }),
                switchMap((app: WorkflowData) => {
                    return this.dataService.commitAssociatedDocuments(app.applicationNumber, this.form.get('documentation').get('confidential').value);
                })
            );
        }
        
        this._loader.toggleLoader(true);
        const subs$: Subscription = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this._loader.toggleLoader(false);
                },
                (error) => {
                    console.error(error);
                    this._loader.reset();
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }
            );
        this.subscription.add(subs$);
    }

    buildConstructionForm(): FormGroup {
        let obj: any = {};
        this.casingTypes.forEach((type) => {
            obj[type.code] = this.buildSubConstructionForm(type);
            this.numericFields.push(`construction|${type.code}|boreholeDiameter`);
            this.numericFields.push(`construction|${type.code}|boreholeDepth`);
            this.numericFields.push(`construction|${type.code}|casingDiameter`);
            this.numericFields.push(`construction|${type.code}|casingDepth`);
            this.numericFields.push(`construction|${type.code}|cementTop`);
            this.numericFields.push(`construction|${type.code}|cementSacks`);
            this.numericFields.push(`construction|${type.code}|centralizers`);
            this.booleanFields.push('construction|${type.code}|tested');
        });
        return this.builder.group(obj);
    }

    buildSubConstructionForm(type: CasingTypeModel): FormGroup {
        let subObj: any = {};
        subObj["definition"] = [{ value: type.definition, disabled: true }, []];
        subObj["boreholeDiameter"] = [{ value: null, disabled: true }, []];
        subObj["boreholeDepth"] = [{ value: null, disabled: true }, []];
        subObj["casingDiameter"] = [{ value: null, disabled: true }, []];
        subObj["casingDepth"] = [{ value: null, disabled: true }, []];
        subObj["cementTop"] = [{ value: null, disabled: true }, []];
        subObj["cementSacks"] = [{ value: null, disabled: true }, []];
        subObj["formation"] = [{ value: null, disabled: true }, []];
        subObj["drillingEquipment"] = [{ value: null, disabled: true }, []];
        subObj["centralizers"] = [{ value: null, disabled: true }, []];
        subObj["tested"] = [{ value: null, disabled: true }, []];
        return this.builder.group(subObj);
    }

    buildForm(): FormGroup {
        let form = this.builder.group({
            applicationStep: [ApplicationSteps.VerifyOwner, []],
            organization: this.builder.group({
                name: [{ value: "", disabled: true }],
                address: this.builder.group({
                    street: [{ value: "", disabled: true }, []],
                    suite: [{ value: "", disabled: true }, []],
                    poBox: [{ value: "", disabled: true }, []],
                    city: [{ value: "", disabled: true }, []],
                    state: [{ value: "", disabled: true }, []],
                    zip: [{ value: "", disabled: true }, []]
                }),
                id: [{ value: "", disabled: true }],
                phone: [{ value: "", disabled: true }],
                email: [{ value: "", disabled: true }]
            }),
            permitType: this.builder.group({
                type: [{ value: null, disabled: true }, [Validators.required]],
                existingAPI: [{ value: null, disabled: true }, []],
                expedite: [{ value: null, disabled: true }, [Validators.required]],
                orientation: [{ value: null, disabled: true }, [Validators.required]]
            }),
            wellInformation: this.builder.group({
                name: [{ value: null, disabled: true }, [Validators.required]],
                number: [{ value: null, disabled: true }, [Validators.required]],
                surfaceLatitude: [{ value: null, disabled: true }, [Validators.required, Validators.min(0)]],
                surfaceLongitude: [{ value: null, disabled: true }, [Validators.required, Validators.max(0)]],
                targetLatitude: [{ value: null, disabled: true }],
                targetLongitude: [{ value: null, disabled: true }],
                section: [{ value: null, disabled: true }],
                lot: [{ value: null, disabled: true }],
                tract: [{ value: null, disabled: true }],
                civilTownship: [{ value: null, disabled: true }],
                civilTownshipNumber: [{ value: null, disabled: true }],
                countyName: [{ value: null, disabled: true }],
                countyNumber: [{ value: null, disabled: true }],
                surfaceElevation: [{ value: null, disabled: true }, [Validators.required]],
                proposedTotalDepth: [{ value: null, disabled: true }, [Validators.required, Validators.min(0)]],
                proposedFormation: [{ value: [], disabled: true }, [Validators.required, Validators.minLength(1)]],
                surfaceGISValidationRun: [{ value: null, disabled: true }, [Validators.requiredTrue]],
                surfaceRights: [{ value: null, disabled: true }, []],
                agency: [{ value: [], disabled: true }, []]
            }),
            waterSource: this.builder.group({
                source: [{ value: null, disabled: true }, [Validators.required]],
                latitude: [{ value: null, disabled: true }, []],
                longitude: [{ value: null, disabled: true }, []],
                watershed: [{ value: null, disabled: true }, [Validators.required]],
                watershedOther: [{ value: null, disabled: true }, []],
                withdrawalRate: [{ value: null, disabled: true }, [Validators.required, Validators.min(0)]],
                totalVolume: [{ value: null, disabled: true }, [Validators.required, Validators.min(0)]],
                recycledWater: [{ value: null, disabled: true }, [Validators.required, Validators.min(0)]],
                watershedValidated: [null, []]
            }),
            contacts: this.builder.group({
                fire: this.builder.group({ name: [{ value: null, disabled: true }, [Validators.required]], phone: [{ value: null, disabled: true }, [Validators.required, Validators.pattern('[- +()0-9]+')]] }),
                police: this.builder.group({ name: [{ value: null, disabled: true }, [Validators.required]], phone: [{ value: null, disabled: true }, [Validators.required, Validators.pattern('[- +()0-9]+')]] }),
                medical: this.builder.group({ name: [{ value: null, disabled: true }, [Validators.required]], phone: [{ value: null, disabled: true }, [Validators.required, Validators.pattern('[- +()0-9]+')]] }),
                emaDirector: this.builder.group({ name: [{ value: null, disabled: true }, []], phone: [{ value: null, disabled: true }, [Validators.pattern('[- +()0-9]+')]] }),
                planningCommittee: this.builder.group({ name: [{ value: null, disabled: true }, []], phone: [{ value: null, disabled: true }, [Validators.pattern('[- +()0-9]+')]] }),
                emergency: this.builder.group({
                    company: [{ value: null, disabled: true }, [Validators.required]],
                    contact: [{ value: null, disabled: true }, [Validators.required]],
                    title: [{ value: null, disabled: true }, []],
                    phone: [{ value: null, disabled: true }, [Validators.required, Validators.pattern('[- +()0-9]+')]],
                    email: [{ value: null, disabled: true }, [Validators.email]]
                })
            }),
            access: this.builder.group({
                description: [{ value: null, disabled: true }, [Validators.required]],
                latitude: [{ value: null, disabled: true }, [Validators.min(0)]],
                longitude: [{ value: null, disabled: true }, [Validators.max(0)]]
            }),
            construction: this.buildConstructionForm(),
            documentation: this.builder.group({
                plat: [{ value: [], disabled: true }, [Validators.required, Validators.minLength(1)]],
                urbanizedArea: [{ value: null, disabled: true }, [Validators.required]],
                aerialPhotography: [{ value: [], disabled: true }, []],
                aerialPhotographyWithContours: [{ value: [], disabled: true }, []],
                urbanAreaSwornStatementAffidavit: [{ value: [], disabled: true }, []],
                miscellaneousDocuments: [{ value: [], disabled: true }, []],
                confidential: [{ value: false, disabled: true }, []],
                restorationPlan: [{ value: [], disabled: true }, []]

            }),
            signature: this.builder.group({
                signature: [{ value: null, disabled: true }, [Validators.required]],
                affirmation: [{ value: null, disabled: true }, [Validators.requiredTrue]]
            }),
            payment: this.builder.group({
                step: [PaymentSteps.PaymentType, []],
                paymentType: [{ value: null, disabled: true }, [Validators.required]],
                feesDue: [null, []],
                payments: [null, []],
                creditAuthorization: [[], []],
                invoice: [null, [Validators.required]]
            }),
            review: this.builder.group({
                completeDate: [{ value: null, disabled: this.application.applicationState !== ApplicationState.VerifyCompletion }, [Validators.required]],
                wellClass: ["STRT", []],
                updatedAPI: [null, [Validators.minLength(14), Validators.maxLength(14), Validators.pattern(new RegExp("^[0-9]"))]],
                specialConditions: this.builder.group({
                    twentyFortyAcre: [[], []],
                    annualDisposal: [[], []],
                    annularDisposal: [[], []],
                    ashtabulaDevonianFluidRotary: [[], []],
                    ashtabulaDevonianOriskany: [[], []],
                    enhancedRecovery: [[], []],
                    conductorMineStringComboBasket: [[], []],
                    directionalDrilling: [[], []],
                    failureToMaintainInsurance: [[], []],
                    fluidRotaryLakePlainOriskany: [[], []],
                    horizontalDrilling: [[], []],
                    hydrogenSulfide: [[], []],
                    ielSuperfund: [[], []],
                    knoxUnconformity: [[], []],
                    lakePlainRegionOriskany: [[], []],
                    lakePlainRegionUtica: [[], []],
                    mineStringBasketVoid: [[], []],
                    mineStringBasketGreaterThreeHundred: [[], []],
                    mineStringBasketLessThanThreeHundred: [[], []],
                    municipalWellheadProtection: [[], []],
                    municipalWellheadProtectionQueenston: [[], []],
                    noPerforationZone: [[], []],
                    noiseMitigation: [[], []],
                    oldWellUnknownStatus: [[], []],
                    otherConditions: [[], []],
                    prePermittingShaleSite: [[], []],
                    prePermittingUrbanSite: [[], []],
                    removalOfFluidHorizontalShale: [[], []],
                    seismicActivity: [[], []],
                    shallowGas: [[], []],
                    ssm1: [[], []],
                    ssm4: [[], []],
                    swiw: [[], []],
                    surfaceCasingMineStringCombo: [[], []],
                    surfaceCasingMineStringComboBasket: [[], []],
                    thinGlacialTillQueenston: [[], []],
                    thinGlacialTillClinton: [[], []],
                    trempealeau: [[], []],
                    wellInAOR: [[], []]
                })
            })
        });
        return this.restoreFormData(form);
    }

    confirmApprove(): void {
        this.form.enable();
        this.form.updateValueAndValidity();
        if (this.form.valid) {
            this.resetInvalid();
            this.approve();
        } else {
            this.invalid.toggleDialog(true);
            this.dialogService.updateDialog(this.invalid);
        }
    }

    generateApplicationPaymentParameters(): ApplicationPaymentParameters {
        return {
            applicationId: this.application.id,
            applicationNumber: this.application.applicationNumber,
            applicationType: ApplicationType.Stratigraphic,
            newWell: this.form.get('permitType').get('existingAPI').value == null,
            permitType: this.form.get('permitType').get('type').value == 'Drill' ? 'New Well' : this.form.get('permitType').get('type').value,
            wellType: 'STRAT',
            wellTypeDescription: '',
            fluidProposed: null,
            expeditePermit: this.showFeeSimple,
            unitMPn: null,
            urbanArea: this.form.get('documentation').get('urbanizedArea').value
        } as ApplicationPaymentParameters;
    }

    next(): void {
        let currentStep = this.form.get('applicationStep').value;
        let nextStep = currentStep + 1;        
        if (nextStep <= 10) {
            this.form.get('applicationStep').setValue(nextStep);
        }
        else if (nextStep == 11) {
            nextStep = 0;
            this.form.get('applicationStep').setValue(nextStep);
        }
    }

    onReceiptIssued(save: boolean): void {
        if (save) {
            this.save(false);
        }
    }

    previous(): void {
        let currentStep = this.form.get('applicationStep').value;
        let nextStep = currentStep - 1;
        if (nextStep >= 0) {
            this.form.get('applicationStep').setValue(nextStep);
        } else if (nextStep < 0) {
            nextStep = 10;
            this.form.get('applicationStep').setValue(nextStep);
        }
    }

    resetInvalid(): void {
        this.form.disable();
        this.form.get('applicationStep').setValue(ApplicationSteps.Review);
        this.form.get('review').enable();
        this.form.get('review').updateValueAndValidity();
    }


    returnApplication(): void {
        let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let state: ApplicationState = (app.applicationState == ApplicationState.InitialApproval)
                    ? ApplicationState.InitialReview
                    : (app.applicationState == ApplicationState.FinalApproval)
                        ? ApplicationState.InitialApproval
                        : ApplicationState.Corrections;

                
                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: state
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );
        this._loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this.returnDialog.message = this.returnDialog.message.replace('[APPNUMBER]', app.applicationNumber);
                    let state: string = app.applicationState == ApplicationState.Corrections
                        ? "returned for corrections"
                        : "returned for additional review";
                    this.returnDialog.message = this.returnDialog.message.replace('[STATE]', state);
                    this.returnDialog.toggleDialog(true);
                    this._loader.toggleLoader(false);
                    this.dialogService.updateDialog(this.returnDialog);
                },
                (error) => {
                    console.error(error);
                    this.dialogService.showError();
                    this._loader.toggleLoader(false);
                }
            );
        this.subscription.add(subs$);
    }

    setAuthorizationRecordIds(): void {
        //do not delete!  meant to override and prevent calling the base class's method, but review form shouldn't be rewriting these fields itself
    }

    stepChanged(newStep: ApplicationSteps): void {
        if (this.form == null || this.form.get('applicationStep') == null) return;
        this.form.get('applicationStep').setValue(newStep);
    }




}
